import { useState, useRef, useEffect } from 'react';
import type { DutchVerb, Person } from '@homeapp/shared';
import { PERSON_LABELS } from '@homeapp/shared';

interface Props {
  verb: DutchVerb;
  person: Person;
  onSubmit: (answer: string) => void;
  onExplain: (word: string) => void;
  disabled?: boolean;
}

export default function ConjugationExercise({ verb, person, onSubmit, onExplain, disabled }: Props) {
  const [answer, setAnswer] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setAnswer('');
    inputRef.current?.focus();
  }, [verb.verbId, person]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (answer.trim()) onSubmit(answer.trim());
  };

  return (
    <div className="space-y-6">
      {/* Verb info */}
      <div className="text-center">
        <div className="text-sm text-gray-500 uppercase tracking-wide mb-1">Conjugation</div>
        <div className="text-2xl font-bold text-gray-900">{verb.infinitive}</div>
        <div className="text-lg text-gray-600">({verb.english})</div>
        {verb.separable && (
          <div className="text-sm text-purple-600 mt-1">⚡ scheidbaar werkwoord</div>
        )}
        {verb.irregular && (
          <div className="text-sm text-orange-600 mt-1">⚠ onregelmatig</div>
        )}
      </div>

      {/* Exercise */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-gray-50 rounded-xl p-6 text-center">
          <div className="text-lg text-gray-700 mb-3">
            <span className="font-medium">{PERSON_LABELS[person]}</span>
          </div>
          <div className="flex items-center justify-center gap-3">
            <span className="text-xl font-medium text-gray-800">{person === 'ik' ? 'Ik' : person.charAt(0).toUpperCase() + person.slice(1)}</span>
            <input
              ref={inputRef}
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              disabled={disabled}
              className="border-b-2 border-purple-400 bg-transparent text-center text-xl font-medium text-gray-900 focus:border-purple-600 focus:outline-none w-48 py-1"
              placeholder="..."
              autoComplete="off"
              autoCapitalize="off"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={disabled || !answer.trim()}
            className="flex-1 bg-purple-600 text-white py-3 px-6 rounded-xl font-medium text-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Check ✓
          </button>
          <button
            type="button"
            onClick={() => onExplain(verb.infinitive)}
            className="bg-gray-200 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-300 transition-colors"
            title="Get explanation"
          >
            💡
          </button>
        </div>
      </form>

      {/* Difficulty badge */}
      <div className="text-center">
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          Difficulty: {Array.from({ length: verb.difficulty }, (_, i) => (
            <span key={i}>⭐</span>
          ))}
        </span>
      </div>
    </div>
  );
}
